/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import _ from 'lodash'
import { Button } from '@styles/button.style'
import { CheerForTeamArea, TotalTabWrap, TxtLimit } from '@styles/base.style'
import { ListView, Tabs } from 'antd-mobile'
import { Models } from 'rmc-tabs/lib/Models'
import { CheerForTeamListBtnStyle } from '@views/Popup/Chat/index.style'
import { SportsIco } from '@styles/match.style'
import { SPORT_ID_ICON_MAP } from '@shared/SportIdGroup'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'
import { FavoriteIco } from '@shared/Icon/MatchupIcon'
import FavoriteImgGray from '@assets/images/btn/favorite-manage-gray.png'
import FavoriteActiveImg from '@assets/images/btn/favorite-manage-active.png'
import { AdvEmpty } from '@components/AdvEmpty'
import { EMPTY_ICON } from '@constants/index'
import useRootStore from '@store/useRootStore'
import { CHEER_FOR_TEAM_MATCH_SUB_TYPE, CHEER_FOR_TEAM_MATCH_TYPE, Schema$CheerForTeamData } from '@protocol/chat'
import dayjs from 'dayjs'
import classNames from 'classnames'
import {
  formatNumber,
  isNotStartedMatchStatus,
  isOngoingMatchStatus,
  isPeriodScoreMatch,
  listViewScrollMoveTop,
} from '@utils/utils'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
import * as ImageUtils from '@utils/image-utils'
import { MatchStatusTag } from '@components/MatchStatusTag'

const dataSource = new ListView.DataSource({
  rowHasChanged: (row1, row2) => row1 !== row2,
})

interface CheerForTeamProps {
  onCloseCheerForTeam: () => void
  isEmoticon?: boolean
}

export const CheerForTeam: React.FC<CheerForTeamProps> = observer((props) => {
  const { onCloseCheerForTeam, isEmoticon } = props
  const { chattingStore, matchupStore } = useRootStore()
  const {
    cheerForTeamMatchList,
    cheerForTeamMatchTotalCountPerTab,
    cheerForTeamMatchListHasMore,
    currentCheerForTeamType,
    currentCheerForTeamSubType,
  } = chattingStore

  const [autoSwitchMainType, setAutoSwitchMainType] = useState(true)
  const [autoSwitchSubType, setAutoSwitchSubType] = useState(true)

  const FavoriteActiveBtn = useMemo(() => {
    return <img src={FavoriteActiveImg} className="favorite-btn-active-img" alt="favorite-btn-active"/>
  }, [])

  const fetchData = (): void => {
    chattingStore.fetchCheerForTeamMatchList({
      cheerForTeamType: currentCheerForTeamType,
      cheerForTeamSubType: currentCheerForTeamSubType,
      autoSwitchMainType,
      autoSwitchSubType,
      reset: true,
    }).then((): void => {
      setTimeout((): void => {
        listViewScrollMoveTop('cheer-for-team-contents-list-view')
      }, 100)
      setAutoSwitchMainType(false)
      setAutoSwitchSubType(false)
    }).catch(console.error)
  }

  useEffect((): void => {
    // 최초 컴포넌트 마운트시 2번 조회되는걸 막기 위해..
    // 아래 useOnMount 에서 최초 조회를 하지 않으면 첫 데이터 조회 후 변경되는 currentCheerForTeam 때문에
    // 데이터 조회를 한번 더 하는 현상을 막기 위해 아래 useOnMount 에서 최초 데이터 로드를 하고
    // 이후 여기에서 메인 탭 변경시 데이터 로드를 한다.
    if (autoSwitchMainType) {
      return
    }
    fetchData()
  }, [currentCheerForTeamType])

  useEffect((): void => {
    // 최초 컴포넌트 마운트시 2번 조회되는걸 막기 위해..
    // 아래 useOnMount 에서 최초 조회를 하지 않으면 첫 데이터 조회 후 변경되는 currentCheerForTeamSubType 때문에
    // 데이터 조회를 한번 더 하는 현상을 막기 위해 아래 useOnMount 에서 최초 데이터 로드를 하고
    // 이후 여기에서 서브 탭 변경시 데이터 로드를 한다.
    if (autoSwitchSubType) {
      return
    }
    fetchData()
  }, [currentCheerForTeamSubType])

  useOnMount((): void => {
    fetchData()
  })

  useOnUnmount((): void => {
    chattingStore.resetCheerForTeamListData(true)
  })

  const cheerForTeamAreaName = classNames('', {
    'pc': !isMobile,
    'is-emoticon': isEmoticon,
  })

  const FilterTabs: Models.TabData[] = [
    {
      title: (
        <div className="etc-league tab-item">
          <span className="txt">마이벳</span>
          <span className="num">{formatNumber(cheerForTeamMatchTotalCountPerTab.myBet)}</span>
        </div>
      ),
      name: '마이베팅경기',
      key: CHEER_FOR_TEAM_MATCH_TYPE.MY_BET.toString(),
    },
    {
      title: (
        <div className="etc-league tab-item favorite-tab">
          <span className="favorite-effect"/>
          <span className="txt favorite">{FavoriteActiveBtn}<FavoriteIco/></span>
          <span className="num">{formatNumber(cheerForTeamMatchTotalCountPerTab.favorite)}</span>
        </div>
      ),
      name: '찜한경기',
      key: CHEER_FOR_TEAM_MATCH_TYPE.FAVORITE.toString(),
    },
    {
      title: (
        <div className="etc-league tab-item">
          <span className="txt">진행중</span>
          <span className="num">{formatNumber(cheerForTeamMatchTotalCountPerTab.live)}</span>
        </div>
      ),
      name: '진행중경기',
      key: CHEER_FOR_TEAM_MATCH_TYPE.LIVE.toString(),
    },
  ]

  const FilterSubTabs = [
    {
      title: (
        <div className="tab-item">
          <span className="txt">진행중</span>
        </div>
      ),
      name: '진행중',
      key: CHEER_FOR_TEAM_MATCH_SUB_TYPE.ONGOING.toString(),
    },
  ]

  if (currentCheerForTeamType !== CHEER_FOR_TEAM_MATCH_TYPE.LIVE) {
    FilterSubTabs.push({
      title: (
        <div className="tab-item">
          <span className="txt">시작전</span>
        </div>
      ),
      name: '시작전',
      key: CHEER_FOR_TEAM_MATCH_SUB_TYPE.NOT_STARTED.toString(),
    })
    FilterSubTabs.push({
      title: (
        <div className="tab-item favorite-tab">
          <span className="txt">종료</span>
        </div>
      ),
      name: '종료',
      key: CHEER_FOR_TEAM_MATCH_SUB_TYPE.CLOSED.toString(),
    })
  }

  const onClickCloseCheerForTeam = (): void => {
    if (onCloseCheerForTeam) {
      chattingStore.resetCheerForTeamListData()
      onCloseCheerForTeam()
    }
  }

  const handleTabsChange = (tab: Models.TabData): void => {
    setAutoSwitchSubType(true)
    chattingStore.setCurrentCheerForTeamType(Number(tab.key))
    chattingStore.setCurrentCheerForTeamSubType(CHEER_FOR_TEAM_MATCH_SUB_TYPE.ONGOING)
  }

  const handleSubTabChange = (tab: Models.TabData): void => {
    chattingStore.setCurrentCheerForTeamSubType(Number(tab.key))
  }

  const onSelectedCheerForTeamHandler = (item: Schema$CheerForTeamData): void => {
    const defaultEffect = ImageUtils.getDefaultCheerForTeamEffectIcon()
    chattingStore.setSelectedCheerForTeamItem({ ...item, effectIconUrl: defaultEffect.img })
    if (onCloseCheerForTeam) {
      onCloseCheerForTeam()
    }
  }

  const onClickFavoriteMatch = async (item: Schema$CheerForTeamData): Promise<void> => {
    // do something
    const { matchId, sportId, dateOfMatch } = item
    const result = await matchupStore.toggleFavoriteMatch({ matchId, sportId, dateOfMatch })
    chattingStore.updateCheerForTeamMatchFavorite(result, matchId)
    if (currentCheerForTeamType === CHEER_FOR_TEAM_MATCH_TYPE.FAVORITE) {
      chattingStore.removeCheerForTeamMatch(matchId)
    }
  }

  const makeSetScoreElems = (
    item: Schema$CheerForTeamData,
  ): { home: React.ReactElement[], away: React.ReactElement[] } => {
    const result = { home: [], away: [] }
    if (_.isEmpty(item.homePeriodScores) || _.isEmpty(item.awayPeriodScores)) {
      return result
    }

    _.range(0, item.homeScore).forEach(idx => {
      result.home.push(<span key={`cheer_for_team_home_set_scores-${idx}`} className={`circle`}/>)
    })
    _.range(0, item.awayScore).forEach(idx => {
      result.away.push(<span key={`cheer_for_team_away_set_scores-${idx}`} className={`circle`}/>)
    })
    return result
  }

  const isFilterTab = currentCheerForTeamType !== 2
  const renderHeader = (): React.ReactElement => {
    return (
      (isFilterTab &&
        <TotalTabWrap round noNum className="cheering-tab-my-bet">
          <Tabs
            tabs={FilterSubTabs}
            initialPage={currentCheerForTeamSubType}
            page={currentCheerForTeamSubType}
            prefixCls="am-tabs"
            onChange={handleSubTabChange}
          />
        </TotalTabWrap>
      )
    )
  }

  const renderRow = (row: Schema$CheerForTeamData): React.ReactElement => {
    const setScoreMap = makeSetScoreElems(row)
    let homeScore = row.homeScore
    let awayScore = row.awayScore
    if (isOngoingMatchStatus(row.matchStatus) && isPeriodScoreMatch(row.sportId)) {
      if (!_.isEmpty(row.homePeriodScores) && !_.isEmpty(row.awayPeriodScores)) {
        homeScore = _.last(row.homePeriodScores)
        awayScore = _.last(row.awayPeriodScores)
      }
    }
    const homeScoreClassNames = classNames('home-team-score', {
      'win': homeScore > awayScore,
    })
    const awayScoreClassNames = classNames('away-team-score', {
      'win': homeScore < awayScore,
    })
    const favoriteBtnClassNames = classNames('favorite-btn', {
      'active': row.isFavoriteMatch,
    })
    return (
      <div
        key={`cheer-for-team_${currentCheerForTeamType}_${row.matchId}`}
        className={!isMobile && 'pc'}
        css={CheerForTeamListBtnStyle}
      >
        <Button
          className={favoriteBtnClassNames}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onClickFavoriteMatch(row).catch(console.error)
            return false
          }}
        >
          <div className="favorite-btn-ele">
            <span className="favorite-pulse"/>
            <span className="favorite-pulse pulse-2"/>
            <img src={FavoriteImgGray} className="favorite-btn-img" alt="favorite-btn"/>
            {FavoriteActiveBtn}
          </div>
        </Button>

        <SportsIco className="sports-ico">
          {SPORT_ID_ICON_MAP[row.sportId] || SPORT_ID_ICON_MAP['178']}
        </SportsIco>
        <div className="date-info-wrap">
          <MatchStatusTag
            matchStatus={row.matchStatus}
            matchStatusName={row.matchStatusName}
            dateOfMatch={row.dateOfMatch}
          />
          <div className="date-info">
            <span className="date">{dayjs(row.dateOfMatch).format('MM/DD(ddd)')}</span>
            <span className="time">{dayjs(row.dateOfMatch).format('HH:mm')}</span>
          </div>
        </div>
        <Button
          TP
          className="match-info-wrap"
          onClick={() => onSelectedCheerForTeamHandler(row)}
        >
          <span className="team-info">
            <span className="team-emblem">
              <img src={row.homeEmblem || BASIC_TEAM_EMBLEM_MAP[row.sportId]} alt="홈팀 엠블럼" className="team-emblem-img"
                   loading="lazy"/>
            </span>
            <span css={TxtLimit} className="home-team-name">{row.homeName}</span>
            <span className={homeScoreClassNames}>
              {isPeriodScoreMatch(row.sportId) && <div className="match-state">{setScoreMap.home}</div>}
              {isNotStartedMatchStatus(row.matchStatus) ? <span className="before-score">-</span> : homeScore}
            </span>
          </span>
          <span className="team-info">
            <span className="team-emblem">
              <img src={row.awayEmblem || BASIC_TEAM_EMBLEM_MAP[row.sportId]} alt="홈팀 엠블럼" className="team-emblem-img"
                   loading="lazy"/>
            </span>
            <span css={TxtLimit} className="away-team-name">{row.awayName}</span>
            <span className={awayScoreClassNames}>
              {isPeriodScoreMatch(row.sportId) && <div className="match-state">{setScoreMap.away}</div>}
              {isNotStartedMatchStatus(row.matchStatus) ? <span className="before-score">-</span> : awayScore}
            </span>
          </span>
        </Button>
      </div>
    )
  }

  const renderFooter = (): React.ReactElement => {
    let footer: React.ReactElement
    if (cheerForTeamMatchList != null && cheerForTeamMatchList.length > 0) {
      footer = <Fragment/>
    } else {
      footer = <AdvEmpty ico={EMPTY_ICON.CHEER_FOR_TEAM_MATCH_LIST}/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  const onEndReached = (): void => {
    if (cheerForTeamMatchListHasMore) {
      chattingStore.fetchCheerForTeamMatchList({
        cheerForTeamType: currentCheerForTeamType,
        cheerForTeamSubType: currentCheerForTeamSubType,
        autoSwitchMainType: false,
        autoSwitchSubType: false,
        reset: false,
      }).catch(console.error)
    }
  }

  const listViewClassNames = classNames('cheer-for-team-contents-list-view filter-list', {
    pc: !isMobile,
  })
  return (
    <CheerForTeamArea className={cheerForTeamAreaName}>
      <div className="cheer-for-team-contents-wrap">
        <Button TP className="cls-btn" onClick={onClickCloseCheerForTeam}>
          <span className="x-ico"/>
        </Button>
        <div className="cheer-for-team-contents">
          <TotalTabWrap whiteUnderline className="cheer-for-team-tab">
            <Tabs
              tabs={FilterTabs}
              prefixCls="am-tabs"
              onChange={handleTabsChange}
              initialPage={currentCheerForTeamType}
              page={currentCheerForTeamType}
              renderTabBar={tabProps => {
                return <Tabs.DefaultTabBar {...tabProps} page={FilterTabs.length}/>
              }}
            />
          </TotalTabWrap>
          <ListView
            key={'cheer-for-team-match-list'}
            className={listViewClassNames}
            css={{ height: 'calc(100% - 40px)' }}
            dataSource={dataSource.cloneWithRows(cheerForTeamMatchList)}
            renderHeader={renderHeader}
            renderRow={renderRow}
            renderFooter={renderFooter}
            onEndReached={onEndReached}
            scrollRenderAheadDistance={500}
            onEndReachedThreshold={10}
            initialListSize={20}
            pageSize={20}
          />
        </div>
      </div>
    </CheerForTeamArea>
  )
})